import React, { useState } from 'react';
import FlexTable from 'components/FlexTable';
import DashboardPanel from 'containers/Dashboard/components/DashboardPanel';
import { PlanPanel } from '../PlanPanel';
import { PlanDetailPanel } from '../PlanDetailPanel';
import { UpdateAmountsModal } from '../UpdateAmountsModal';
import { InviteByEmailModal } from '../InviteByEmailModal';
import { BulkUpdateProgressModal } from '../BulkUpdateProgressModal';
import { CancelPlanModal } from '../CancelPlanModal';
import { Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import __ from 'utils/i18n';
import BottomAlert from 'components/BottomAlert';
import useSuccessAlert from 'hooks/useSuccessAlert';
import { useSelector } from 'react-redux';
import { fetchResourceRP } from '../../../_shared/fetchResourceRP';
import { cancelPlan } from '../../_shared/resources';
import { calculatePlanStatus } from '../../_shared/planStatusUtils';
import { notification } from 'utils/notifications';
import { BackendRpErrorResponse } from '../../../_shared/BackendRpErrorResponse';
import gtmEvents from 'utils/gtmEvents';

const getPlansColumns = (onUpdateAmounts, onEditPlan, onCancelPlan, copiedLinks, handleCopyLink, tooltipStates) => {
  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatFrequency = (frequency) => {
    const frequencies = {
      Daily: __('dashboard.sections.recurringPayments.plans.table.frequencies.daily'),
      Weekly: __('dashboard.sections.recurringPayments.plans.table.frequencies.weekly'),
      Monthly: __('dashboard.sections.recurringPayments.plans.table.frequencies.monthly'),
      Quarterly: __('dashboard.sections.recurringPayments.plans.table.frequencies.quarterly'),
      Yearly: __('dashboard.sections.recurringPayments.plans.table.frequencies.yearly'),
    };
    return frequencies[frequency] || frequency;
  };

  // Calcular la próxima fecha de cobro para Quarterly
  const getNextQuarterlyBillingDate = (billingMonth, billingDay) => {
    const today = new Date();
    const targetMonth = parseInt(billingMonth) - 1; // 0-indexed
    const targetDay = Math.min(parseInt(billingDay), 28);
    
    // Calcular los meses del ciclo trimestral (cada 3 meses)
    const quarterMonths = [];
    for (let i = 0; i < 12; i += 3) {
      quarterMonths.push((targetMonth + i) % 12);
    }
    
    // Buscar la próxima fecha de cobro
    let nextDate = null;
    const currentYear = today.getFullYear();
    
    // Revisar los próximos 4 trimestres (hasta 12 meses adelante)
    for (let yearOffset = 0; yearOffset <= 1; yearOffset++) {
      for (let month of quarterMonths) {
        const candidateDate = new Date(currentYear + yearOffset, month, targetDay);
        if (candidateDate > today) {
          nextDate = candidateDate;
          break;
        }
      }
      if (nextDate) break;
    }
    
    if (!nextDate) return '-';
    
    const day = String(nextDate.getDate()).padStart(2, '0');
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const year = nextDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Calcular la próxima fecha de cobro para Yearly
  const getNextYearlyBillingDate = (billingMonth, billingDay) => {
    const today = new Date();
    const targetMonth = parseInt(billingMonth) - 1; // 0-indexed
    const targetDay = Math.min(parseInt(billingDay), 28);
    
    let nextDate = new Date(today.getFullYear(), targetMonth, targetDay);
    
    // Si la fecha ya pasó este año, usar el próximo año
    if (nextDate <= today) {
      nextDate = new Date(today.getFullYear() + 1, targetMonth, targetDay);
    }
    
    const day = String(nextDate.getDate()).padStart(2, '0');
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const year = nextDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Obtener el texto del tooltip según la frecuencia
  const getBillingDayTooltipText = (plan) => {
    switch (plan.frequency) {
      case 'Weekly':
        if (!plan.billingDay) {
          return __('dashboard.sections.recurringPayments.plans.form.billingDay.subscription');
        }
        const weekDays = [
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.monday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.tuesday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.wednesday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.thursday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.friday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.saturday'),
          __('dashboard.sections.recurringPayments.plans.form.billingDay.weekly.sunday')
        ];
        return `Todos los ${weekDays[plan.billingDay - 1]}`;

      case 'Monthly':
        if (!plan.billingDay) {
          return __('dashboard.sections.recurringPayments.plans.form.billingDay.subscription');
        }
        return __('dashboard.sections.recurringPayments.plans.detail.billingDayFormat', { day: plan.billingDay });

      case 'Quarterly':
        if (plan.billingMonth && plan.billingDay) {
          return getNextQuarterlyBillingDate(plan.billingMonth, plan.billingDay);
        }
        return null;

      case 'Yearly':
        if (plan.billingMonth && plan.billingDay) {
          return getNextYearlyBillingDate(plan.billingMonth, plan.billingDay);
        }
        return null;

      default:
        return null;
    }
  };

  return [
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.plan'),
      Cell: ({ original }) => {
        const name = original.name ? original.name : '-';
        const image = original.planImage?.url || original.productImageUrl;
        const imageClasses = {
          backgroundColor: original.backgroundColor
            ? `#${original.backgroundColor}`
            : '',
        };

        return (
          <div className='catalog__product--name'>
            <span
              style={image ? {} : imageClasses}
              className='catalog__product--image'
            >
              {image ? (
                <img
                  className='img-cover'
                  src={image}
                  alt='Plan'
                  height='45px'
                  width='45px'
                />
              ) : (
                <span>{original.name.charAt(0)}</span>
              )}
            </span>
            <span className="catalog__product--name-text">{name}</span>
          </div>
        );
      },
      width: 300,
    },
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.amount'),
      Cell: ({ original }) => {
        const isFixed = original.pricingType === 'Fixed';
        return (
          <span className="plan-amount text-wrap-amount">
            {isFixed ? 
              formatCurrency(original.amount, original.currency) : 
              __('dashboard.sections.recurringPayments.plans.table.variable')
            }
          </span>
        );
      },
      width: 130,
    },
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.frequency'),
      Cell: ({ original }) => formatFrequency(original.frequency),
      width: 120,
    },
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.duration'),
      Cell: ({ original }) => 
        original.duration ? 
          __('dashboard.sections.recurringPayments.plans.table.durationValue', { count: original.duration }) 
          : __('dashboard.sections.recurringPayments.plans.detail.unlimited'),
      width: 120,
    },
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.subscribers'),
      Cell: ({ original }) => {
        return <span className="subscribers-count">{original.activeSubscriptionsCount || 0}</span>;
      },
      width: 130,
    },
    {
      Header: __('dashboard.sections.recurringPayments.plans.table.status'),
      Cell: ({ original, index }) => {
        const originalStatus = original.status;
        const isActive = originalStatus === 'Active';
        
        // Para planes no activos, mostrar "Cancelado"
        if (!isActive) {
          return (
            <span className="badge badge-secondary">
              {__('dashboard.sections.recurringPayments.plans.table.statuses.cancelled')}
            </span>
          );
        }
        
        // Para planes activos, calcular el estado dinámico
        const calculatedStatus = calculatePlanStatus(original);
        const isChargeDay = calculatedStatus === 'ChargeDay';
        const isNextToCharge = calculatedStatus === 'NextToCharge';
        const isNoAmounts = calculatedStatus === 'NoAmounts';
        const isPendingCharge = calculatedStatus === 'PendingCharge';
        
        let badgeClass = 'secondary';
        let statusText = '';
        
        if (isChargeDay) {
          badgeClass = 'primary';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.chargeDay');
        } else if (isNextToCharge) {
          badgeClass = 'warning';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.nextToCharge');
        } else if (isNoAmounts) {
          badgeClass = 'danger';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.noAmounts');
        } else if (isPendingCharge) {
          badgeClass = 'warning';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.pendingCharge');
        } else {
          badgeClass = 'success';
          statusText = __('dashboard.sections.recurringPayments.plans.table.statuses.active');
        }
        
        const tooltipId = `status-tooltip-${original.planId}-${index}`;
        const infoTooltipId = `status-info-tooltip-${original.planId}-${index}`;
        
        // Mostrar ícono de información para Weekly/Monthly (siempre) o Quarterly/Yearly (si tienen billingDay)
        const showInfoIcon = (isActive || isNextToCharge || isNoAmounts) && 
                             (original.frequency === 'Weekly' || 
                              original.frequency === 'Monthly' || 
                              (original.frequency === 'Quarterly' && original.billingDay && original.billingMonth) ||
                              (original.frequency === 'Yearly' && original.billingDay && original.billingMonth));
        const tooltipText = getBillingDayTooltipText(original);
        
        return (
          <>
            <span 
              id={tooltipId}
              className={`badge badge-${badgeClass}`}
            >
              {statusText}
              {showInfoIcon && tooltipText && (
                <>
                  <i 
                    id={infoTooltipId}
                    className="mdi mdi-information-outline ml-1" 
                    style={{ fontSize: '14px', cursor: 'pointer' }}
                  />
                  <UncontrolledTooltip placement="top" target={infoTooltipId}>
                    {tooltipText}
                  </UncontrolledTooltip>
                </>
              )}
            </span>
            {(isNextToCharge || isPendingCharge) && original.nextChargeDate && (
              <UncontrolledTooltip placement="top" target={tooltipId}>
                {__('dashboard.sections.recurringPayments.plans.table.nextChargeDate')}: {moment(original.nextChargeDate).format('DD/MM/YYYY')}
              </UncontrolledTooltip>
            )}
          </>
        );
      },
      width: 140,
    },
    {
      Header: <div className="text-right">{__('dashboard.sections.recurringPayments.plans.table.actions')}</div>,
      Cell: ({ original }) => {
        const calculatedStatus = calculatePlanStatus(original);
        const isChargeDay = calculatedStatus === 'ChargeDay';
        const isCancelled = original.status === 'Inactive';
        const isFixed = original.pricingType === 'Fixed';
        const hasActiveSubscribers = (original.activeSubscriptionsCount || 0) > 0;
        const isCopied = copiedLinks[original.planId];
        
        // Si es plan cancelado, no mostrar nada
        if (isCancelled) {
          return null;
        }
        
        // Si es plan variable CON suscriptores activos Y NO es día de cobro, mostrar botón de actualizar montos + dropdown
        if (!isFixed && hasActiveSubscribers && !isChargeDay) {
          return (
            <div className="text-right d-flex align-items-center justify-content-end">
              <Button
                id={`update-amounts-btn-${original.planId}`}
                color="primary"
                outline
                size="sm"
                className="font-weight-bold d-inline-flex align-items-center justify-content-center mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateAmounts(original);
                }}
              >
                <i className="mdi mdi-pencil mr-1" />
                {__('dashboard.sections.recurringPayments.plans.table.updateAmount')}
              </Button>
              
              <UncontrolledDropdown>
                <DropdownToggle
                  tag="button"
                  className="btn btn-link text-dark p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="mdi mdi-dots-vertical" style={{ fontSize: '24px' }}></i>
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      if (original.paymentLink) {
                        handleCopyLink(original.planId, original.paymentLink);
                      }
                    }}
                  >
                    {isCopied 
                      ? __('dashboard.sections.recurringPayments.plans.detail.linkCopied') 
                      : __('dashboard.sections.recurringPayments.plans.table.copyLink')}
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPlan(original);
                    }}
                  >
                    {__('dashboard.sections.recurringPayments.plans.actions.editPlan')}
                  </DropdownItem>
                  <DropdownItem
                    className="text-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCancelPlan(original);
                    }}
                  >
                    {__('dashboard.sections.recurringPayments.plans.actions.cancelPlan')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        }
        
        // Para todos los demás casos (Fixed o Variable sin suscriptores), mostrar "Copiar link" + dropdown
        const buttonText = isCopied 
          ? __('dashboard.sections.recurringPayments.plans.detail.linkCopied') 
          : __('dashboard.sections.recurringPayments.plans.table.copyLink');
        
        return (
          <div className="text-right d-flex align-items-center justify-content-end">
            <Button
              id={`copy-link-btn-${original.planId}`}
              color="primary"
              outline
              size="sm"
              className="font-weight-bold d-inline-flex align-items-center justify-content-center mr-2"
              onClick={(e) => {
                e.stopPropagation();
                if (original.paymentLink) {
                  handleCopyLink(original.planId, original.paymentLink);
                }
              }}
            >
              <i className={`mdi mdi-${isCopied ? 'check' : 'content-copy'} mr-1`} />
              {buttonText}
            </Button>
            
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                className="btn btn-link text-dark p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="mdi mdi-dots-vertical" style={{ fontSize: '24px' }}></i>
              </DropdownToggle>
              <DropdownMenu right>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPlan(original);
                  }}
                >
                  {__('dashboard.sections.recurringPayments.plans.actions.editPlan')}
                </DropdownItem>
                <DropdownItem
                  className="text-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelPlan(original);
                  }}
                >
                  {__('dashboard.sections.recurringPayments.plans.actions.cancelPlan')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        );
      },
      cancelClick: true,
      width: 220,
    },
  ];
};

export const PlansTable = ({ plans, reload, showDetails, setShowDetails, selectedPlan, setSelectedPlan, onViewSubscribers }) => {
  const { token } = useSelector((state) => state.api);
  const merchantId = useSelector((state) => state.login.user?.merchantId);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [planToUpdate, setPlanToUpdate] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [planToInvite, setPlanToInvite] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedPlanIdToEdit, setSelectedPlanIdToEdit] = useState(null);
  const [copiedLinks, setCopiedLinks] = useState({});
  const [tooltipStates] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [planToCancel, setPlanToCancel] = useState(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressData, setProgressData] = useState(null);
  
  // Hook para manejar alertas de éxito
  const { alertProps } = useSuccessAlert();

  const getSelectedPlanToEdit = () => {
    return plans.find((plan) => plan.planId === selectedPlanIdToEdit) || null;
  };

  const handleDetails = (data) => {
    setSelectedPlan(data);
    setShowDetails(true);
    setShowEditPanel(false);
  };

  const handleUpdateAmounts = (plan) => {
    setPlanToUpdate(plan);
    setShowUpdateModal(true);
  };

  const handleInviteByEmail = (plan) => {
    setPlanToInvite(plan);
    setShowInviteModal(true);
  };

  const handleStartProcessing = (data) => {
    // Guardar el plan en progressData para poder volver si hay error
    setProgressData({
      ...data,
      plan: planToUpdate
    });
    setShowProgressModal(true);
  };

  const handleProgressRefresh = () => {
    if (reload) {
      reload();
    }
  };

  const handleProgressSuccess = () => {
    setShowProgressModal(false);
    setProgressData(null);
    handleProgressRefresh();
  };

  const handleGoBack = () => {
    setShowProgressModal(false);
    // Si hay un plan guardado en progressData, abrir el modal de actualización con ese plan
    if (progressData?.plan) {
      setPlanToUpdate(progressData.plan);
      setShowUpdateModal(true);
    }
    setProgressData(null);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlanIdToEdit(plan.planId);
    setShowEditPanel(true);
    setShowDetails(false);
  };

  const handlePlanEdited = (editedPlan) => {
    setShowEditPanel(false);
    notification.success(__('dashboard.sections.recurringPayments.plans.planSavedSuccess'));
    setSelectedPlan(editedPlan);
    setShowDetails(true);
    if (reload) {
      reload();
    }
  };

  const handleCancelPlan = (plan) => {
    setPlanToCancel(plan);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async (plan) => {
    setIsCanceling(true);
    try {
      await fetchResourceRP(() => cancelPlan(plan, token));

      gtmEvents.recurringPlanCancelSuccess({
        plan_id: plan.planId,
        merchant_id: merchantId,
      });
      notification.success(__('dashboard.sections.recurringPayments.plans.planCanceledSuccess'));
      setShowCancelModal(false);
      setPlanToCancel(null);
      
      if (showDetails && selectedPlan?.planId === plan.planId) {
        setShowDetails(false);
        setSelectedPlan(null);
      }
      
      if (reload) {
        reload();
      }
    } catch (error) {
      gtmEvents.recurringPlanCancelError({
        plan_id: plan.planId,
        merchant_id: merchantId,
        error_code: error?.message_code,
      });
      let errorMessage = __('dashboard.sections.recurringPayments.plans.planCanceledError');
      notification.error(errorMessage + '\n ' + BackendRpErrorResponse.firstErrorMessage(error));

    } finally {
      setIsCanceling(false);
    }
  };

  const handleCopyLink = (planId, paymentLink) => {
    navigator.clipboard.writeText(paymentLink).then(() => {
      setCopiedLinks(prev => ({ ...prev, [planId]: true }));
      notification.success(__('dashboard.sections.recurringPayments.plans.detail.linkCopiedMessage'));
      
      setTimeout(() => {
        setCopiedLinks(prev => ({ ...prev, [planId]: false }));
      }, 3000);
    });
  };


  const getEditPanelTitle = () => {
    const planToEdit = getSelectedPlanToEdit();
    if (!planToEdit || !planToEdit.planId) {
      return __('dashboard.sections.recurringPayments.plans.form.title');
    }
    return __('dashboard.sections.recurringPayments.plans.form.editTitle');
  };

  return (
    <div className="table-container">
      {/* Panel de detalle del plan */}
      <DashboardPanel
        panelHeading={__('dashboard.sections.recurringPayments.plans.detail.title')}
        expanded={showDetails && !showEditPanel}
        hidePanel={() => setShowDetails(false)}
      >
        {showDetails && !showEditPanel && (
          <PlanDetailPanel 
            plan={selectedPlan} 
            onEdit={handleEditPlan}
            onUpdateAmounts={handleUpdateAmounts}
            onInviteByEmail={handleInviteByEmail}
            onCancel={handleCancelPlan}
            onViewSubscribers={onViewSubscribers}
          />
        )}
      </DashboardPanel>

      {/* Panel de edición del plan */}
      <DashboardPanel
        panelHeading={getEditPanelTitle()}
        expanded={showEditPanel}
        hidePanel={() => setShowEditPanel(false)}
      >
        {showEditPanel && (
          <PlanPanel 
            data={getSelectedPlanToEdit()} 
            onSave={handlePlanEdited}
            hidePanel={() => setShowEditPanel(false)}
            showDetails={showEditPanel}
          />
        )}
      </DashboardPanel>

      <UpdateAmountsModal
        showModal={showUpdateModal}
        hideModal={() => {
          setShowUpdateModal(false);
          setPlanToUpdate(null);
        }}
        plan={planToUpdate}
        onStartProcessing={handleStartProcessing}
      />

      <InviteByEmailModal
        showModal={showInviteModal}
        hideModal={() => {
          setShowInviteModal(false);
          setPlanToInvite(null);
        }}
        plan={planToInvite}
      />

      <BulkUpdateProgressModal
        showModal={showProgressModal}
        hideModal={() => {
          setShowProgressModal(false);
          setProgressData(null);
        }}
        totalRows={progressData?.totalRows || 0}
        streamHubTopic={progressData?.streamHubTopic || null}
        onSuccess={handleProgressSuccess}
        onRefresh={handleProgressRefresh}
        error={progressData?.error || null}
        errorMessage={progressData?.errorMessage || null}
        onGoBack={handleGoBack}
        pendingUpload={progressData?.pendingUpload || false}
        file={progressData?.file || null}
        plan={progressData?.plan || null}
      />

      <CancelPlanModal
        showModal={showCancelModal}
        hideModal={() => {
          setShowCancelModal(false);
          setPlanToCancel(null);
        }}
        plan={planToCancel}
        submitAction={() => handleConfirmCancel(planToCancel)}
        submitDisabled={isCanceling}
      />
      
      {plans && plans.length > 0 && (
        <FlexTable
          data={plans}
          columns={getPlansColumns(handleUpdateAmounts, handleEditPlan, handleCancelPlan, copiedLinks, handleCopyLink, tooltipStates)}
          onRowClick={({ index }) => handleDetails(plans[index])}
        />
      )}

      <BottomAlert {...alertProps} />
    </div>
  );
};
